import React, { KeyboardEvent, ReactNode, RefObject, useCallback, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CloseIcon } from '../../icons';
import './Sheet.css';

export interface SheetProps {
  open: boolean;
  title: string;
  onClose: () => void;
  initialFocusRef?: RefObject<HTMLElement>;
  returnFocusRef?: RefObject<HTMLElement>;
  closeLabel?: string;
  id?: string;
  describedBy?: string;
  className?: string;
  children: ReactNode;
}

const FOCUSABLE = 'summary, a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusables(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((element) => {
    if (element.tabIndex < 0 || element.matches(':disabled')) return false;
    for (let ancestor: HTMLElement | null = element; ancestor; ancestor = ancestor.parentElement) {
      if (ancestor.hidden || ancestor.hasAttribute('inert')) return false;
      if (ancestor instanceof HTMLDetailsElement && !ancestor.open) {
        const summary = Array.from(ancestor.children).find((child) => child.tagName === 'SUMMARY');
        if (!summary?.contains(element)) return false;
      }
      const style = window.getComputedStyle(ancestor);
      if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') return false;
      if (ancestor === root) break;
    }
    return true;
  });
}

/**
 * Modal sheet: labelled dialog, focus trap, inert background, Escape and a
 * visible close control, focus restored to the opener on close. Bottom sheet
 * on phone, centered from tablet up. Reduced motion renders instantly.
 */
const Sheet: React.FC<SheetProps> = ({
  open,
  title,
  onClose,
  initialFocusRef,
  returnFocusRef,
  closeLabel = 'Close',
  id,
  describedBy,
  className,
  children,
}) => {
  const generatedId = useId();
  const sheetId = id ?? `luma-sheet-${generatedId}`;
  const titleId = `${sheetId}-title`;
  const rootRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const restoreFocus = useCallback(
    (opener: HTMLElement | null) => {
      const restoreTo = returnFocusRef?.current ?? opener;
      if (restoreTo && typeof restoreTo.focus === 'function' && document.contains(restoreTo)) {
        restoreTo.focus();
      }
    },
    [returnFocusRef]
  );

  useEffect(() => {
    if (!open) return undefined;
    const opener = (document.activeElement as HTMLElement | null) ?? null;
    openerRef.current = opener;

    const dialog = dialogRef.current;
    const preferred = initialFocusRef?.current;
    const candidates = focusables(dialog).filter((element) => element !== closeRef.current);
    const target = preferred ?? candidates[0] ?? closeRef.current ?? dialog;
    target?.focus();

    const root = rootRef.current;
    const siblings = Array.from(document.body.children).filter(
      (element) => element !== root && element.tagName !== 'SCRIPT'
    ) as HTMLElement[];
    const previouslyInert = siblings.filter((element) => element.hasAttribute('inert'));
    siblings.forEach((element) => element.setAttribute('inert', ''));
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      siblings.forEach((element) => {
        if (!previouslyInert.includes(element)) element.removeAttribute('inert');
      });
      document.body.style.overflow = previousOverflow;
      restoreFocus(opener);
    };
  }, [open, initialFocusRef, restoreFocus]);

  if (!open || typeof document === 'undefined') return null;

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onCloseRef.current();
      return;
    }
    if (event.key !== 'Tab') return;
    const items = focusables(dialogRef.current);
    if (items.length === 0) {
      event.preventDefault();
      dialogRef.current?.focus();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement as HTMLElement | null;
    if (event.shiftKey && (active === first || !dialogRef.current?.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !dialogRef.current?.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div ref={rootRef} className="luma-theme luma-sheet-root" onKeyDown={onKeyDown}>
      <div className="luma-sheet__backdrop" onClick={() => onCloseRef.current()} data-testid={`${sheetId}-backdrop`} />
      <div
        ref={dialogRef}
        id={sheetId}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={describedBy}
        tabIndex={-1}
        className={`luma-sheet${className ? ` ${className}` : ''}`}
      >
        <div className="luma-sheet__head">
          <h2 id={titleId} className="luma-sheet__title">{title}</h2>
          <button ref={closeRef} type="button" className="luma-sheet__close" onClick={() => onCloseRef.current()} aria-label={closeLabel}>
            <CloseIcon size={22} />
          </button>
        </div>
        <div className="luma-sheet__body">{children}</div>
      </div>
    </div>,
    document.body
  );
};

export default Sheet;
